import { createContext, useContext, useEffect, useRef, useState, FormEvent, ReactNode, CSSProperties } from 'react';
import { supabase } from '../lib/supabase';
import { cachedRpc } from '../lib/rpcCache';
import { Icon } from './Icon';

// A single, app-wide delivery detail panel. Both search boxes (sidebar and
// the home hero) open into the same modal instance via this context, so two
// panels can never stack on top of each other.
const DeliveryDetailContext = createContext<((hit: StopHit) => void) | null>(null);

export function DeliveryDetailProvider({ children }: { children: ReactNode }) {
  const [selected, setSelected] = useState<StopHit | null>(null);
  return (
    <DeliveryDetailContext.Provider value={(hit) => setSelected(hit)}>
      {children}
      {selected && <DeliveryDetailModal hit={selected} onClose={() => setSelected(null)} />}
    </DeliveryDetailContext.Provider>
  );
}

export function WSHeartMark({
  size = 32, frontColor = '#3B9EFF', backColor = '#12355B',
}: { size?: number; frontColor?: string; backColor?: string }) {
  // Locate2u location-pin mark: navy teardrop with a bright-blue centre.
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ display: 'block', flexShrink: 0 }}>
      <path
        d="M32 4c-11.046 0-20 8.447-20 18.867 0 13.2 16.706 28.933 18.693 30.747a1.93 1.93 0 0 0 2.614 0C35.294 51.8 52 36.067 52 22.867 52 12.447 43.046 4 32 4z"
        fill={backColor}
      />
      <circle cx="32" cy="22.5" r="8.5" fill={frontColor} />
    </svg>
  );
}

export function WSLogo({ size = 22, mono = false }: { size?: number; mono?: boolean }) {
  const height = size * 1.5;
  return (
    <img
      src="/assets/locate2u-classic.png"
      alt="Locate2u"
      style={{ height, width: 'auto', display: 'block', filter: mono ? 'brightness(0)' : 'none', flexShrink: 0 }}
    />
  );
}

export function DemoBanner() {
  return (
    <div
      role="note"
      aria-label="Demo disclaimer"
      style={{
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '6px 16px',
        background: 'var(--ws-blue-wash)',
        borderBottom: '1px solid var(--ws-line-soft)',
        color: 'var(--ws-blue-deep)',
        fontFamily: 'var(--ws-sans)', fontSize: 12, lineHeight: 1.3,
        textAlign: 'center',
      }}
    >
      <span style={{
        flexShrink: 0,
        fontFamily: 'var(--ws-mono)', fontSize: 9.5, fontWeight: 600,
        letterSpacing: '0.14em', textTransform: 'uppercase',
        padding: '2px 7px', borderRadius: 4,
        background: 'var(--ws-blue)', color: 'var(--ws-white)',
      }}>
        Demo
      </span>
      <span>
        This is a demonstration environment — all figures, names, and deliveries
        shown are <strong style={{ fontWeight: 600 }}>synthetic sample data</strong>, not real customer information.
      </span>
    </div>
  );
}

// A shimmering placeholder shown in place of content while an RPC is in
// flight. `light` switches to the on-dark variant for the blue headline tiles.
export function Skeleton({
  width, height = '1em', radius = 4, light = false, style,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number;
  light?: boolean;
  style?: CSSProperties;
}) {
  return (
    <span
      aria-hidden
      className={light ? 'ws-skeleton ws-skeleton-light' : 'ws-skeleton'}
      style={{ width, height, borderRadius: radius, ...style }}
    />
  );
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

// A number that counts up to its target and flashes when it settles on a new
// value, so a viewer actually notices data arriving or a filter re-running.
// While `loading` (or value is null) it renders a skeleton instead. Respects
// prefers-reduced-motion via the CSS (the flash/skeleton simply don't animate).
export function AnimatedNumber({
  value,
  format = (n) => Math.round(n).toLocaleString(),
  loading = false,
  durationMs = 650,
  light = false,
  skeletonWidth = 72,
  emptyLabel = '—',
  className,
  style,
}: {
  value: number | null | undefined;
  format?: (n: number) => string;
  loading?: boolean;
  durationMs?: number;
  light?: boolean;
  skeletonWidth?: number | string;
  emptyLabel?: string;
  className?: string;
  style?: CSSProperties;
}) {
  const [display, setDisplay] = useState<number | null>(loading ? null : (value ?? null));
  const [flashKey, setFlashKey] = useState(0);
  const prevRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (loading) return;                       // hold last value; skeleton shows
    if (value == null) { setDisplay(null); return; }
    const from = prevRef.current == null ? 0 : prevRef.current;
    prevRef.current = value;
    if (from === value) { setDisplay(value); return; }

    setFlashKey((k) => k + 1);
    let startTs: number | null = null;
    const tick = (ts: number) => {
      if (startTs == null) startTs = ts;
      const t = Math.min(1, (ts - startTs) / durationMs);
      setDisplay(from + (value - from) * easeOutCubic(t));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value, loading, durationMs]);

  if (loading) {
    return <Skeleton width={skeletonWidth} height="0.9em" light={light} style={style} />;
  }
  if (display == null) {
    return <span className={['ws-num', className].filter(Boolean).join(' ')} style={style}>{emptyLabel}</span>;
  }
  return (
    <span className={['ws-num', className].filter(Boolean).join(' ')} style={{ display: 'inline-block', ...style }}>
      <span
        key={flashKey}
        className={flashKey === 0 ? undefined : (light ? 'ws-value-flash-light' : 'ws-value-flash')}
        style={{ display: 'inline-block' }}
      >
        {format(display)}
      </span>
    </span>
  );
}

export function Avatar({
  initials, color, size = 28,
}: { initials: string; color?: string; size?: number }) {
  const palette = ['#0a0a0a', '#3a3a3a', '#6b6258', '#c8322d', '#2f7a3f', '#1c1c1c'];
  const bg = color || palette[initials.charCodeAt(0) % palette.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: bg,
      color: 'var(--ws-bone)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.4, fontWeight: 600, letterSpacing: '0.04em',
      fontFamily: 'var(--ws-sans)', flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

export function StatusPill({
  status, small = false,
}: { status: { label: string; dot: string }; small?: boolean }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: small ? '2px 0' : '4px 0',
      fontSize: small ? 11.5 : 12.5,
      color: 'var(--ws-ink-2)',
      whiteSpace: 'nowrap',
    }}>
      <span className={`ws-dot ws-dot-${status.dot}`} />
      {status.label}
    </span>
  );
}

export function Sparkline({
  data, width = 120, height = 32, color = 'var(--ws-blue)', fill = false,
}: { data: number[]; width?: number; height?: number; color?: string; fill?: boolean }) {
  if (!data || data.length === 0) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y] as [number, number];
  });
  const path = points.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(2) + ' ' + p[1].toFixed(2)).join(' ');
  const areaPath = path + ` L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }}>
      {fill && <path d={areaPath} fill={color} opacity="0.14" />}
      <path d={path} fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="3" fill={color} />
    </svg>
  );
}

export type Screen = 'dashboard' | 'failed' | 'difot' | 'feedback' | 'driver_performance' | 'delivery_cost' | 'board_report' | 'users';

export function Sidebar({
  current, onNav, onLogout, user, isAdmin = false,
}: {
  current: Screen;
  onNav: (s: Screen) => void;
  onLogout: () => void;
  user: { name: string; role: string; initials: string };
  isAdmin?: boolean;
}) {
  const items: { id: Screen | string; label: string; icon: any; disabled?: boolean }[] = [
    { id: 'failed', label: 'Failed deliveries', icon: 'alert' },
    { id: 'difot', label: 'DIFOT', icon: 'clock' },
    { id: 'driver_performance', label: 'Driver performance', icon: 'truck' },
    { id: 'feedback', label: 'Customer feedback', icon: 'star' },
    { id: 'delivery_cost', label: 'Delivery cost', icon: 'dollar' },
  ];

  return (
    <aside style={{
      width: 240, background: 'var(--ws-paper)', borderRight: '1px solid var(--ws-line-soft)',
      display: 'flex', flexDirection: 'column', flexShrink: 0, height: '100%',
    }}>
      <button
        onClick={() => onNav('dashboard')}
        title="Home"
        style={{
          display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
          background: 'transparent', border: 0, padding: '26px 22px 22px',
        }}
      >
        <WSLogo size={20} />
        <div style={{
          marginTop: 14, fontFamily: 'var(--ws-mono)', fontSize: 10.5,
          letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ws-mute)',
        }}>
          Reporting Portal
        </div>
      </button>

      <div style={{ padding: '0 12px 4px' }}>
        <DeliverySearch variant="sidebar" />
      </div>

      <div style={{ padding: '0 12px', display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        <div className="ws-eyebrow" style={{ padding: '12px 10px 8px', fontSize: 9.5 }}>Overview</div>
        {items.map(it => (
          <NavItem
            key={it.id}
            item={it}
            active={current === it.id}
            onClick={() => !it.disabled && onNav(it.id as Screen)}
          />
        ))}

        {isAdmin && (<>
          <div className="ws-eyebrow" style={{ padding: '20px 10px 8px', fontSize: 9.5 }}>Leadership</div>
          <NavItem
            item={{ id: 'board_report', label: 'Board report', icon: 'grid' }}
            active={current === 'board_report'}
            onClick={() => onNav('board_report')}
          />
        </>)}

        <div className="ws-eyebrow" style={{ padding: '20px 10px 8px', fontSize: 9.5 }}>Team</div>
        <NavItem
          item={{ id: 'users', label: 'Users', icon: 'people' }}
          active={current === 'users'}
          onClick={() => onNav('users')}
        />
      </div>

      <div style={{ borderTop: '1px solid var(--ws-line-soft)' }}>
        <SyncStatus />
        <div style={{
          padding: '4px 16px 16px',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <Avatar initials={user.initials} size={32} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ws-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.name}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ws-mute)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.role}
            </div>
          </div>
          <button onClick={onLogout} title="Sign out" style={{
            background: 'transparent', border: 0, padding: 6, color: 'var(--ws-mute)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="logout" size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}

type StopHit = {
  stop_id: number;
  team_id: number;
  source_reference: string | null;
  status: string | null;
  address_city: string | null;
  address_province: string | null;
  arrival_date: string | null;
  customer_name: string | null;
};

const STATUS_HIT_COLOR: Record<string, string> = {
  Complete: 'var(--ws-success)',
  Failed: 'var(--ws-danger)',
  Cancelled: 'var(--ws-mute)',
};

// Quick delivery lookup. Types a reference into a fast, all-history search
// (quick_find_stops RPC) and shows matches in a dropdown; each opens the
// stop in Locate2u. Enter opens the first match. Two sizes: the compact
// "sidebar" variant and a larger "hero" variant for the home page.
export function DeliverySearch({ variant = 'sidebar' }: { variant?: 'sidebar' | 'hero' }) {
  const hero = variant === 'hero';
  const [value, setValue] = useState('');
  const [results, setResults] = useState<StopHit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const term = value.trim();
    if (term.length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    let cancelled = false;
    const id = setTimeout(() => {
      supabase.rpc('quick_find_stops', { p_search: term, p_limit: hero ? 10 : 8 }).then(({ data, error }) => {
        if (cancelled) return;
        setLoading(false);
        setResults(error ? [] : ((data as StopHit[]) ?? []));
      });
    }, 300);
    return () => { cancelled = true; clearTimeout(id); };
  }, [value, hero]);

  // Close the dropdown when clicking outside the search.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const openDelivery = useContext(DeliveryDetailContext);

  function selectHit(hit: StopHit) {
    setOpen(false);
    openDelivery?.(hit);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (results && results.length > 0) selectHit(results[0]);
  }

  const showPanel = open && value.trim().length >= 2;
  const spin = hero ? 16 : 13;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <form
        onSubmit={submit}
        style={{
          display: 'flex', alignItems: 'center', gap: hero ? 11 : 8,
          padding: hero ? '14px 18px' : '8px 11px',
          border: `1px solid ${hero ? 'var(--ws-line-strong)' : 'var(--ws-line)'}`,
          borderRadius: hero ? 8 : 4,
          background: hero ? 'var(--ws-paper)' : 'var(--ws-bone)',
          boxShadow: hero ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
        }}
      >
        <Icon name="search" size={hero ? 18 : 14} color="var(--ws-mute)" />
        <input
          value={value}
          onChange={e => { setValue(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          autoFocus={hero}
          placeholder={hero ? 'Search a delivery by reference…' : 'Search a delivery…'}
          aria-label="Search a delivery by reference"
          style={{
            flex: 1, minWidth: 0, border: 0, background: 'transparent', outline: 'none',
            fontSize: hero ? 15.5 : 12.5, color: 'var(--ws-ink)',
          }}
        />
        {loading && (
          <svg className="ws-spin" width={spin} height={spin} viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="var(--ws-blue)" strokeWidth="2" strokeDasharray="40" strokeDashoffset="20" />
          </svg>
        )}
      </form>

      {showPanel && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 40,
          background: 'var(--ws-paper)', border: '1px solid var(--ws-line)',
          borderRadius: hero ? 8 : 6,
          boxShadow: '0 14px 32px rgba(0,0,0,0.16)', overflow: 'hidden',
          maxHeight: hero ? 420 : 360, overflowY: 'auto', textAlign: 'left',
        }}>
          {loading && (!results || results.length === 0) ? (
            <div style={{ padding: '13px 15px', fontSize: hero ? 13 : 12, color: 'var(--ws-mute)' }}>Searching…</div>
          ) : results && results.length === 0 ? (
            <div style={{ padding: '13px 15px', fontSize: hero ? 13 : 12, color: 'var(--ws-mute)' }}>
              No deliveries match “{value.trim()}”.
            </div>
          ) : (
            (results ?? []).map(r => <SearchHit key={`${r.team_id}:${r.stop_id}`} hit={r} onOpen={selectHit} hero={hero} />)
          )}
        </div>
      )}
    </div>
  );
}

function SearchHit({ hit, onOpen, hero }: { hit: StopHit; onOpen: (hit: StopHit) => void; hero?: boolean }) {
  const dot = STATUS_HIT_COLOR[hit.status ?? ''] ?? 'var(--ws-mute)';
  const place = [hit.customer_name, hit.address_city, hit.address_province].filter(Boolean).join(' · ');
  const date = hit.arrival_date
    ? new Date(hit.arrival_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  return (
    <button
      onClick={() => onOpen(hit)}
      style={{
        display: 'flex', flexDirection: 'column', gap: 3, width: '100%', textAlign: 'left',
        padding: hero ? '11px 15px' : '9px 12px', background: 'transparent', border: 0,
        borderBottom: '1px solid var(--ws-line-soft)', cursor: 'pointer',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--ws-bone-2)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, flexShrink: 0 }} />
        <span className="ws-mono" style={{
          fontSize: hero ? 13 : 12, fontWeight: 600, color: 'var(--ws-ink)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {hit.source_reference || `Stop ${hit.stop_id}`}
        </span>
        <span style={{ marginLeft: 'auto', flexShrink: 0, display: 'inline-flex' }}>
          <Icon name="chevron_right" size={hero ? 14 : 12} color="var(--ws-mute-2)" />
        </span>
      </div>
      {(place || date) && (
        <div style={{
          fontSize: hero ? 12 : 11, color: 'var(--ws-mute)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {place}{place && date ? ' · ' : ''}{date}
        </div>
      )}
    </button>
  );
}

// Shows when the underlying data was actually last synced, read from the
// sync worker's records (data_last_synced RPC) and shown in the viewer's
// local time. Re-checks every few minutes so a fresh sync surfaces without
// a reload. Dot turns amber/red as the data ages.
function SyncStatus() {
  // undefined = still loading; null = unavailable.
  const [syncedAt, setSyncedAt] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    function load() {
      supabase.rpc('data_last_synced').then(({ data, error }) => {
        if (cancelled) return;
        setSyncedAt(error ? null : (data as string | null));
      });
    }
    load();
    const id = setInterval(load, 5 * 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  let dot = 'var(--ws-mute)';
  let text = 'Sync status unavailable';
  let title: string | undefined;

  if (syncedAt === undefined) {
    text = 'Checking sync…';
  } else if (syncedAt) {
    const d = new Date(syncedAt);
    const ageH = (Date.now() - d.getTime()) / 3_600_000;
    dot = ageH <= 3 ? 'var(--ws-success)' : ageH <= 24 ? '#c9921f' : 'var(--ws-danger)';
    text = 'Synced · ' + d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
    title = 'Last successful sync: ' + d.toLocaleString();
  }

  return (
    <div title={title} style={{ padding: '12px 16px 0', display: 'flex', alignItems: 'center', gap: 7 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, flexShrink: 0 }} />
      <span style={{
        fontSize: 10.5, color: 'var(--ws-mute)', fontFamily: 'var(--ws-mono)',
        letterSpacing: '0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {text}
      </span>
    </div>
  );
}

type StopDetail = StopHit & {
  stop_ref: string | null;
  trip_id: number | null;
  departure_date: string | null;
  duration_minutes: number | null;
  rating: number | null;
  load_quantity: number | null;
  eta_time_window_per_trip_started: string | null;
  eta_window_start: string | null;
  eta_window_end: string | null;
  tz: string | null;
  difot_category: 'early' | 'on_time' | 'late' | 'no_eta' | null;
  team_member_name: string | null;
  team_region_name: string | null;
};

const CATEGORY_BADGE: Record<string, { label: string; bg: string; fg: string }> = {
  early:   { label: 'Early',   bg: 'var(--ws-blue-wash)', fg: 'var(--ws-blue-deep)' },
  on_time: { label: 'On time', bg: 'rgba(47,122,63,0.14)', fg: 'var(--ws-success)' },
  late:    { label: 'Late',    bg: 'rgba(200,50,45,0.14)', fg: 'var(--ws-danger)' },
  no_eta:  { label: 'No ETA',  bg: 'var(--ws-bone-2)', fg: 'var(--ws-ink-3)' },
};

function fmtDateTimeTz(iso: string | null, tz: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz,
    }).format(new Date(iso));
  } catch { return new Date(iso).toLocaleString(); }
}
function tzAbbr(iso: string | null, tz: string): string {
  if (!iso) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short', timeZone: tz }).formatToParts(new Date(iso));
    return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
  } catch { return ''; }
}

// In-portal delivery detail. Opened from a search result; fetches the full
// stop via the stop_detail RPC and shows it in a modal with a link out to
// Locate2u.
function DeliveryDetailModal({ hit, onClose }: { hit: StopHit; onClose: () => void }) {
  const [detail, setDetail] = useState<StopDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(false); setDetail(null);
    cachedRpc('stop_detail', { p_team_id: hit.team_id, p_stop_id: hit.stop_id }).then(({ data, error }) => {
      if (cancelled) return;
      setLoading(false);
      if (error || !data) { setError(true); return; }
      setDetail(data as StopDetail);
    });
    return () => { cancelled = true; };
  }, [hit.team_id, hit.stop_id]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const d = detail;
  const tz = d?.tz || 'Australia/Sydney';
  const ref = d?.source_reference || hit.source_reference || `Stop ${hit.stop_id}`;
  const cat = d?.difot_category ? CATEGORY_BADGE[d.difot_category] : null;
  const statusColor = STATUS_HIT_COLOR[(d?.status ?? hit.status) ?? ''] ?? 'var(--ws-mute)';
  const locate2uUrl = `https://app.locate2u.com/stops/details/${hit.stop_id}`;

  return (
    <div
      onMouseDown={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,32,0.46)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onMouseDown={e => e.stopPropagation()}
        className="ws-fadein"
        style={{
          width: 'min(540px, 100%)', maxHeight: '88vh', overflow: 'auto',
          background: 'var(--ws-paper)', borderRadius: 10, color: 'var(--ws-ink)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12, padding: '20px 22px 16px',
          borderBottom: '1px solid var(--ws-line-soft)',
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="ws-eyebrow-brand" style={{ marginBottom: 6 }}>Delivery</div>
            <div className="ws-mono" style={{ fontSize: 20, fontWeight: 700, color: 'var(--ws-ink)', wordBreak: 'break-all' }}>
              {ref}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px',
                borderRadius: 999, background: 'var(--ws-bone-2)', fontSize: 11.5, fontWeight: 600,
              }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor }} />
                {(d?.status ?? hit.status) || 'Unknown'}
              </span>
              {cat && (
                <span style={{ padding: '3px 10px', borderRadius: 999, background: cat.bg, color: cat.fg, fontSize: 11.5, fontWeight: 600 }}>
                  {cat.label}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} title="Close" style={{
            background: 'transparent', border: 0, padding: 6, cursor: 'pointer', color: 'var(--ws-mute)',
            display: 'flex', flexShrink: 0,
          }}>
            <Icon name="close" size={18} />
          </button>
        </div>

        <div style={{ padding: '8px 22px 18px' }}>
          {loading ? (
            <div style={{ padding: '34px 0', textAlign: 'center', color: 'var(--ws-mute)', fontSize: 13 }}>
              <svg className="ws-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 10 }}>
                <circle cx="12" cy="12" r="9" stroke="var(--ws-blue)" strokeWidth="2" strokeDasharray="40" strokeDashoffset="20" />
              </svg>
              Loading delivery…
            </div>
          ) : error || !d ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--ws-mute)', fontSize: 13 }}>
              Couldn't load this delivery's details.
            </div>
          ) : (
            <>
              <DetailRow label="Customer" value={d.customer_name || '—'} />
              <DetailRow label="Driver" value={d.team_member_name || 'Unassigned'} />
              <DetailRow label="Team region" value={d.team_region_name || 'Unassigned'} />
              <DetailRow label="Location" value={[d.address_city, d.address_province].filter(Boolean).join(', ') || '—'} />
              <DetailRow
                label="Arrival"
                value={d.arrival_date ? `${fmtDateTimeTz(d.arrival_date, tz)} ${tzAbbr(d.arrival_date, tz)}` : '—'}
              />
              <DetailRow
                label="ETA window"
                value={
                  d.eta_time_window_per_trip_started
                    ? `${d.eta_time_window_per_trip_started}${tzAbbr(d.arrival_date, tz) ? ' ' + tzAbbr(d.arrival_date, tz) : ''}`
                    : 'No ETA sent'
                }
              />
              {d.departure_date && <DetailRow label="Departure" value={`${fmtDateTimeTz(d.departure_date, tz)} ${tzAbbr(d.departure_date, tz)}`} />}
              {d.duration_minutes != null && <DetailRow label="Duration" value={`${d.duration_minutes} min`} />}
              {d.rating != null && <DetailRow label="Rating" value={`${'★'.repeat(d.rating)}${'☆'.repeat(Math.max(0, 5 - d.rating))}  ${d.rating}/5`} />}
              <DetailRow label="Trip" value={d.trip_id != null ? String(d.trip_id) : '—'} />
              <DetailRow label="Stop ID" value={String(d.stop_id)} mono />
            </>
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
          padding: '14px 22px', borderTop: '1px solid var(--ws-line-soft)', background: 'var(--ws-paper-2)',
        }}>
          <button onClick={onClose} className="ws-btn ws-btn-ghost" style={{ padding: '9px 16px', fontSize: 12.5 }}>
            Close
          </button>
          <a
            href={locate2uUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ws-btn ws-btn-primary"
            style={{ padding: '9px 16px', fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 7, textDecoration: 'none' }}
          >
            Open in Locate2u
            <Icon name="external" size={13} color="currentColor" />
          </a>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{
      display: 'flex', gap: 16, padding: '9px 0', borderBottom: '1px solid var(--ws-line-soft)',
    }}>
      <div style={{ width: 110, flexShrink: 0, fontSize: 11, color: 'var(--ws-mute)', fontFamily: 'var(--ws-mono)', letterSpacing: '0.06em', textTransform: 'uppercase', paddingTop: 1 }}>
        {label}
      </div>
      <div className={mono ? 'ws-mono' : undefined} style={{ fontSize: 13.5, color: 'var(--ws-ink-2)', minWidth: 0, wordBreak: 'break-word' }}>
        {value}
      </div>
    </div>
  );
}

function NavItem({
  item, active, onClick,
}: { item: { id: string; label: string; icon: any; disabled?: boolean }; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={item.disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
        background: active ? 'var(--ws-bone-2)' : 'transparent',
        border: 0, borderRadius: 2,
        color: active ? 'var(--ws-ink)' : (item.disabled ? 'var(--ws-mute-2)' : 'var(--ws-ink-3)'),
        fontSize: 13.5, fontWeight: active ? 600 : 500, textAlign: 'left',
        cursor: item.disabled ? 'default' : 'pointer',
        opacity: item.disabled ? 0.55 : 1, transition: 'background 140ms',
        width: '100%', position: 'relative',
      }}
    >
      <Icon name={item.icon} size={15} />
      <span>{item.label}</span>
      {active && <span style={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 2, background: 'var(--ws-blue)' }} />}
    </button>
  );
}

export function TopBar({
  title, subtitle, breadcrumb, right,
}: {
  title: string;
  subtitle?: string;
  breadcrumb?: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div style={{
      padding: '24px 36px 20px', borderBottom: '1px solid var(--ws-line-soft)',
      background: 'var(--ws-paper)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24,
    }}>
      <div style={{ minWidth: 0 }}>
        {breadcrumb && (
          <div style={{
            marginBottom: 8, fontSize: 10.5, color: 'var(--ws-mute)',
            fontFamily: 'var(--ws-mono)', letterSpacing: '0.16em', textTransform: 'uppercase',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {breadcrumb}
          </div>
        )}
        <h1 className="ws-display-thin" style={{ margin: 0, fontSize: 36, color: 'var(--ws-ink)' }}>
          {title}
        </h1>
        {subtitle && (
          <div style={{ marginTop: 8, fontSize: 14, color: 'var(--ws-mute)' }}>{subtitle}</div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>{right}</div>
    </div>
  );
}

export function SearchBox({
  value, onChange, placeholder = 'Search orders, suburbs, crew…',
}: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
      border: '1px solid var(--ws-line)', borderRadius: 2,
      background: 'var(--ws-bone)', width: 320, color: 'var(--ws-mute)',
    }}>
      <Icon name="search" size={14} />
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          flex: 1, border: 0, background: 'transparent', outline: 'none',
          fontSize: 13, color: 'var(--ws-ink)',
        }}
      />
      <span className="ws-mono" style={{
        fontSize: 10.5, color: 'var(--ws-mute-2)',
        border: '1px solid var(--ws-line)', borderRadius: 2, padding: '1px 5px',
      }}>⌘K</span>
    </div>
  );
}

export function DateRangePill({ label = 'Last 12 weeks', onClick }: { label?: string; onClick?: () => void }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px',
      border: '1px solid var(--ws-line)', borderRadius: 2,
      background: 'var(--ws-bone)', fontSize: 13, color: 'var(--ws-ink-2)', cursor: 'pointer',
    }}>
      <Icon name="calendar" size={14} />
      <span>{label}</span>
      <Icon name="chevron_down" size={13} color="var(--ws-mute)" />
    </button>
  );
}

const SHIMMER_BG = 'linear-gradient(90deg, var(--ws-bone-2) 0%, var(--ws-paper) 50%, var(--ws-bone-2) 100%)';

export function SkeletonBlock({
  width, height, radius = 3, style,
}: { width?: number | string; height: number | string; radius?: number; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        width: width ?? '100%',
        height,
        borderRadius: radius,
        background: SHIMMER_BG,
        backgroundSize: '200% 100%',
        animation: 'ws-shimmer 1.4s linear infinite',
        ...style,
      }}
    />
  );
}

function SkeletonTopBar() {
  return (
    <div style={{
      padding: '24px 36px 20px', borderBottom: '1px solid var(--ws-line-soft)',
      background: 'var(--ws-paper)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24,
    }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <SkeletonBlock width={140} height={11} style={{ marginBottom: 14 }} />
        <SkeletonBlock width={260} height={36} radius={4} />
        <SkeletonBlock width={420} height={14} style={{ marginTop: 12 }} />
      </div>
      <SkeletonBlock width={108} height={34} radius={2} />
    </div>
  );
}

function SkeletonFilterBar() {
  return (
    <div style={{
      padding: '14px 36px', background: 'var(--ws-paper)',
      borderBottom: '1px solid var(--ws-line-soft)',
      display: 'flex', gap: 20, alignItems: 'flex-end', flexWrap: 'wrap',
    }}>
      {[180, 240, 220, 220, 220].map((w, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SkeletonBlock width={70} height={9} />
          <SkeletonBlock width={w} height={32} radius={2} />
        </div>
      ))}
    </div>
  );
}

function SkeletonKpiStrip() {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1,
      background: 'var(--ws-line-soft)', border: '1px solid var(--ws-line-soft)',
      borderRadius: 6, overflow: 'hidden',
    }}>
      {[0, 1, 2, 3].map(i => (
        <div key={i} style={{
          background: 'var(--ws-paper)', padding: '22px 22px 20px',
          display: 'flex', flexDirection: 'column', gap: 10, minHeight: 142,
        }}>
          <SkeletonBlock width={90} height={10} />
          <SkeletonBlock width={120} height={36} radius={4} style={{ marginTop: 4 }} />
          <SkeletonBlock width="80%" height={12} style={{ marginTop: 'auto' }} />
        </div>
      ))}
    </div>
  );
}

function SkeletonCard({ height = 280 }: { height?: number }) {
  return (
    <div className="ws-card" style={{ padding: 22, background: 'var(--ws-paper)' }}>
      <SkeletonBlock width={90} height={10} />
      <SkeletonBlock width={200} height={20} radius={3} style={{ marginTop: 10 }} />
      <SkeletonBlock width={300} height={12} style={{ marginTop: 8 }} />
      <SkeletonBlock height={height} radius={4} style={{ marginTop: 18 }} />
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <SkeletonTopBar />
      <SkeletonFilterBar />
      <div className="ws-scroll" style={{ flex: 1, overflow: 'auto', padding: '24px 36px 60px', background: 'var(--ws-bone)' }}>
        <SkeletonKpiStrip />
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 18, marginTop: 18 }}>
          <SkeletonCard height={240} />
          <SkeletonCard height={240} />
        </div>
        <div style={{ marginTop: 18 }}>
          <SkeletonCard height={260} />
        </div>
      </div>
    </div>
  );
}
